import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatRupiah, formatErr } from "../lib/api";
import { toast } from "sonner";
import {
  User as UserIcon, Crown, ShoppingBag, Brain, Ticket, Lock, Award,
  CheckCircle2, Sparkles, Calendar, ChevronRight, Copy
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Sparkles },
  { id: "profile", label: "Profil", icon: UserIcon },
  { id: "subscription", label: "Membership", icon: Crown },
  { id: "orders", label: "Pesanan", icon: ShoppingBag },
  { id: "tests", label: "Tes & Hasil", icon: Brain },
  { id: "vouchers", label: "Voucher", icon: Ticket },
  { id: "security", label: "Keamanan", icon: Lock },
];

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [tab, setTab] = useState("overview");
  const [form, setForm] = useState({ name: "", phone: "", bio: "", avatar: "" });
  const [orders, setOrders] = useState([]);
  const [results, setResults] = useState([]);
  const [accessTests, setAccessTests] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", bio: user.bio || "", avatar: user.avatar || "" });
    api.get("/orders/me").then(r => setOrders(r.data)).catch(() => {});
    api.get("/test-results/me").then(r => setResults(r.data)).catch(() => {});
    api.get("/users/me/test-access").then(r => setAccessTests(r.data)).catch(() => {});
    api.get("/users/me/vouchers").then(r => setVouchers(r.data)).catch(() => {});
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await updateProfile(form); toast.success("Profil tersimpan"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error("Password baru tidak sama"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password diubah");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sub = user?.subscription;
  const subActive = sub && new Date(sub.expires_at) > new Date();

  return (
    <div className="lumina-container py-12" data-testid="profile-page">
      <div className="grid lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <div className="lumina-card p-6 sticky top-28">
            <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#E2DFED]">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] flex items-center justify-center text-white font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div>
                <div className="font-medium text-[#1A1728]">{user?.name?.split(" ")[0]}</div>
                <div className="text-xs text-[#5C5870]">{user?.email}</div>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map(t => (
                <button
                  key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`profile-tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${tab === t.id ? "bg-[#E8F0FA] text-[#6B4BA3] font-medium" : "text-[#5C5870] hover:bg-[#F1EBFA]/40"}`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </nav>
            <div className="mt-6 pt-6 border-t border-[#E2DFED]">
              <Link to="/redeem" className="flex items-center gap-2 text-sm text-[#6B4BA3] font-medium hover:underline" data-testid="sidebar-redeem">
                🎁 Punya kode lisensi? <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </aside>

        {/* Content */}
        <section className="lg:col-span-3">
          {tab === "overview" && (
            <div data-testid="tab-overview">
              <div className="lumina-label mb-3">Dashboard</div>
              <h1 className="font-serif text-4xl text-[#1A1728] mb-8">Halo, {user?.name?.split(" ")[0]} ✨</h1>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <StatCard icon={ShoppingBag} label="Total Pesanan" value={orders.length} />
                <StatCard icon={Brain} label="Tes Diselesaikan" value={results.length} />
                <StatCard icon={Award} label="Akses Tes" value={accessTests.length} />
              </div>

              {subActive ? (
                <div className="lumina-card p-7 bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] text-white mb-6">
                  <div className="flex items-center gap-2 mb-2"><Crown className="w-4 h-4" /> <span className="text-xs uppercase tracking-widest opacity-90">Membership Aktif</span></div>
                  <div className="font-serif text-3xl mb-1">{sub.plan_title}</div>
                  <div className="text-sm opacity-90">Berlaku hingga {new Date(sub.expires_at).toLocaleDateString("id-ID", { day:"numeric", month:"long", year:"numeric" })}</div>
                </div>
              ) : (
                <div className="lumina-card p-7 mb-6 border-dashed">
                  <Crown className="w-6 h-6 text-[#6B4BA3] mb-2" />
                  <div className="font-serif text-2xl text-[#1A1728] mb-1">Belum berlangganan</div>
                  <p className="text-sm text-[#5C5870] mb-4">Upgrade ke HearInsight+ untuk akses penuh ke semua tes dan konseling.</p>
                  <Link to="/subscriptions" className="lumina-btn-primary inline-flex">Lihat Plan</Link>
                </div>
              )}

              {results.length > 0 && (
                <>
                  <h2 className="font-serif text-2xl text-[#1A1728] mb-4 mt-8">Tes Terakhir</h2>
                  <div className="space-y-3">
                    {results.slice(0, 3).map(r => <TestResultRow key={r.id} r={r} />)}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "profile" && (
            <form onSubmit={saveProfile} className="lumina-card p-8 space-y-4" data-testid="tab-profile-form">
              <h2 className="font-serif text-3xl text-[#1A1728] mb-4">Profil</h2>
              <div>
                <label className="lumina-label block mb-2">Email</label>
                <input disabled value={user?.email} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] bg-[#F1EBFA]/40 text-[#5C5870]" />
              </div>
              <div>
                <label className="lumina-label block mb-2">Nama</label>
                <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="profile-name" />
              </div>
              <div>
                <label className="lumina-label block mb-2">No. WhatsApp</label>
                <input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="profile-phone" />
              </div>
              <div>
                <label className="lumina-label block mb-2">URL Foto Profil</label>
                <input value={form.avatar} onChange={e=>setForm({...form,avatar:e.target.value})} placeholder="https://…" className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
              </div>
              <div>
                <label className="lumina-label block mb-2">Bio</label>
                <textarea value={form.bio} onChange={e=>setForm({...form,bio:e.target.value})} rows={3} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
              </div>
              <button disabled={saving} className="lumina-btn-primary" data-testid="profile-save">{saving ? "Menyimpan…" : "Simpan"}</button>
            </form>
          )}

          {tab === "subscription" && (
            <div className="lumina-card p-8" data-testid="tab-subscription">
              <h2 className="font-serif text-3xl text-[#1A1728] mb-6">Membership</h2>
              {subActive ? (
                <div className="bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] rounded-3xl p-8 text-white">
                  <Crown className="w-8 h-8 mb-3" />
                  <div className="font-serif text-3xl mb-2">{sub.plan_title}</div>
                  <div className="opacity-90 mb-4">Aktif sampai {new Date(sub.expires_at).toLocaleDateString("id-ID", { day:"numeric", month:"long", year:"numeric" })}</div>
                  <div className="opacity-80 text-sm">Dimulai: {new Date(sub.started_at).toLocaleDateString("id-ID")}</div>
                </div>
              ) : (
                <div className="text-center py-10">
                  <Crown className="w-12 h-12 text-[#6B4BA3] mx-auto mb-3" />
                  <p className="text-[#5C5870] mb-5">Kamu belum memiliki membership aktif.</p>
                  <Link to="/subscriptions" className="lumina-btn-primary inline-flex">Pilih Plan</Link>
                </div>
              )}
            </div>
          )}

          {tab === "orders" && (
            <div data-testid="tab-orders">
              <h2 className="font-serif text-3xl text-[#1A1728] mb-6">Riwayat Pesanan</h2>
              <div className="space-y-3">
                {orders.length === 0 && <p className="text-[#5C5870]">Belum ada pesanan.</p>}
                {orders.map(o => (
                  <div key={o.id} className="lumina-card p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="font-mono text-sm font-semibold text-[#1A1728]">{o.order_number}</div>
                        <div className="text-xs text-[#5C5870]">{new Date(o.created_at).toLocaleString("id-ID")}</div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${o.payment_status === "paid" || o.payment_status === "redeemed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{o.payment_status}</div>
                    </div>
                    <div className="text-sm text-[#5C5870] mb-3">{o.items.map(i => i.title).join(", ")}</div>
                    <div className="text-[#6B4BA3] font-semibold">{formatRupiah(o.total)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "tests" && (
            <div data-testid="tab-tests">
              <h2 className="font-serif text-3xl text-[#1A1728] mb-2">Tes & Hasil</h2>
              <p className="text-[#5C5870] mb-6">Riwayat tes dan akses tes berbayarmu.</p>

              <div className="lumina-card p-6 mb-6">
                <h3 className="font-serif text-xl text-[#1A1728] mb-4">Akses Tes Aktif</h3>
                {accessTests.length === 0 && <p className="text-sm text-[#5C5870]">Belum ada akses tes berbayar. Beli atau redeem kode lisensi.</p>}
                <div className="grid sm:grid-cols-2 gap-3">
                  {accessTests.map(t => (
                    <Link to={`/tests/${t.slug}/take`} key={t.id} className="flex items-center gap-3 p-3 border border-[#E2DFED] rounded-xl hover:border-[#6B4BA3] transition">
                      <img src={t.image} alt={t.title} className="w-12 h-12 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{t.title}</div>
                        <div className="text-xs text-[#6B4BA3]">Mulai tes →</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <h3 className="font-serif text-xl text-[#1A1728] mb-4">Hasil Tesmu</h3>
              <div className="space-y-3">
                {results.length === 0 && <p className="text-sm text-[#5C5870]">Belum ada hasil tes.</p>}
                {results.map(r => <TestResultRow key={r.id} r={r} />)}
              </div>
            </div>
          )}

          {tab === "vouchers" && (
            <div data-testid="tab-vouchers">
              <h2 className="font-serif text-3xl text-[#1A1728] mb-2">Voucher Tersedia</h2>
              <p className="text-[#5C5870] mb-6">Pakai kode-kode ini saat checkout.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {vouchers.map(v => (
                  <div key={v.code} className="lumina-card p-5 border-dashed border-[#6B4BA3]/40">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-mono font-bold text-lg text-[#6B4BA3]">{v.code}</div>
                      <button onClick={() => { navigator.clipboard.writeText(v.code); toast.success("Tersalin"); }} className="text-[#6B4BA3] hover:bg-[#E8F0FA] p-2 rounded-lg"><Copy className="w-4 h-4" /></button>
                    </div>
                    <div className="text-sm text-[#1A1728]">
                      {v.type === "percent" ? `${v.value}% off` : `Rp ${v.value.toLocaleString("id-ID")} off`}
                      {v.max_discount && v.type === "percent" && <span className="text-[#5C5870]"> (maks. Rp {v.max_discount.toLocaleString("id-ID")})</span>}
                    </div>
                    <div className="text-xs text-[#5C5870] mt-1">Min. belanja Rp {v.min_purchase.toLocaleString("id-ID")} • Berlaku hingga {new Date(v.valid_until).toLocaleDateString("id-ID")}</div>
                  </div>
                ))}
                {vouchers.length === 0 && <p className="text-[#5C5870]">Belum ada voucher aktif.</p>}
              </div>
            </div>
          )}

          {tab === "security" && (
            <form onSubmit={changePassword} className="lumina-card p-8 max-w-xl space-y-4" data-testid="tab-security">
              <h2 className="font-serif text-3xl text-[#1A1728] mb-4">Keamanan</h2>
              <h3 className="font-medium text-[#1A1728]">Ganti Password</h3>
              <input required type="password" placeholder="Password sekarang" value={pwd.current_password} onChange={e=>setPwd({...pwd, current_password: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="pwd-current" />
              <input required type="password" minLength={6} placeholder="Password baru (min. 6)" value={pwd.new_password} onChange={e=>setPwd({...pwd, new_password: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="pwd-new" />
              <input required type="password" minLength={6} placeholder="Konfirmasi password baru" value={pwd.confirm} onChange={e=>setPwd({...pwd, confirm: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="pwd-confirm" />
              <button disabled={saving} className="lumina-btn-primary" data-testid="pwd-submit">{saving ? "Memproses…" : "Ganti Password"}</button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="lumina-card p-5">
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs text-[#5C5870] uppercase tracking-wider">{label}</div>
      <Icon className="w-4 h-4 text-[#6B4BA3]" />
    </div>
    <div className="font-serif text-3xl text-[#1A1728]">{value}</div>
  </div>
);

const TestResultRow = ({ r }) => (
  <div className="lumina-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`result-${r.id}`}>
    <div>
      <div className="text-xs text-[#5C5870] uppercase tracking-wider mb-1">{new Date(r.answered_at).toLocaleDateString("id-ID")}</div>
      <div className="font-medium text-[#1A1728]">{r.test_title}</div>
      <div className="text-sm text-[#6B4BA3]">{r.tier_title} • Skor {r.score}</div>
    </div>
    <div className="flex gap-2">
      <Link to={`/test-results/${r.id}`} className="lumina-btn-outline !py-2 !px-4 text-sm">Detail</Link>
      {r.share_id && <Link to={`/share/test/${r.share_id}`} className="lumina-btn-secondary !py-2 !px-4 text-sm">Share</Link>}
    </div>
  </div>
);
